import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canManageUsers } from '../../utils/rolePermissions';
import { UserRole } from '../../types';
import './SettingsPage.css';

// Lazy imports for settings sub-pages
const UserManagement = React.lazy(() => import('../admin/UserManagement'));
// Sits next to User Management: one creates the employee, the other reads what they sent back.
const EmployeeInformation = React.lazy(() => import('../admin/EmployeeInformation'));
const ExitFormalities = React.lazy(() => import('../admin/ExitFormalities'));
const EmailSettings = React.lazy(() => import('../admin/EmailSettings'));
const NominationSettings = React.lazy(() => import('./NominationSettings'));
const OrgListsSettings = React.lazy(() => import('../admin/OrgListsSettings'));
const CertificationSettings = React.lazy(() => import('./CertificationSettings'));
// Settings → Image Management. Super Admin only; the tab is absent from the HR list below,
// and the website-images Lambda refuses the writes regardless of who reaches the page.
const ImageManagement = React.lazy(() => import('./ImageManagement'));
const AttendanceSettings = React.lazy(() => import('../admin/AttendanceSettings'));
const AttendancePolicySettings = React.lazy(() => import('../attendance/AttendancePolicySettings'));
const SessionTimeoutSettings = React.lazy(() => import('./SessionTimeoutSettings'));

interface SettingsTab {
  id: string;
  label: string;
  icon: string;
}

const SettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isHrAdminOnly = user?.role === UserRole.HR_ADMIN;

  // Organisation Setup sits directly under User Management: it defines the values the User
  // Management form can offer, so HR reaches for it from the same place.
  const tabs: SettingsTab[] = isHrAdminOnly
    ? [
        { id: 'users', label: 'User Management', icon: '👥' },
        { id: 'employee-info', label: 'Employee Information', icon: '🗂️' },
        { id: 'exit-formalities', label: 'Exit Formalities', icon: '🚪' },
        { id: 'org-lists', label: 'Organisation Setup', icon: '🏷️' },
        { id: 'attendance', label: 'Manual Attendance', icon: '🕒' },
        { id: 'attendance-rules', label: 'Attendance Rules', icon: '📋' },
        { id: 'session-timeout', label: 'Session Timeout', icon: '⏱️' },
      ]
    : [
        { id: 'overview', label: 'Overview', icon: '📊' },
        { id: 'users', label: 'User Management', icon: '👥' },
        { id: 'employee-info', label: 'Employee Information', icon: '🗂️' },
        { id: 'exit-formalities', label: 'Exit Formalities', icon: '🚪' },
        { id: 'org-lists', label: 'Organisation Setup', icon: '🏷️' },
        { id: 'attendance', label: 'Manual Attendance', icon: '🕒' },
        { id: 'attendance-rules', label: 'Attendance Rules', icon: '📋' },
        { id: 'email', label: 'Email Settings', icon: '📧' },
        { id: 'nominations', label: 'Nomination Settings', icon: '🏆' },
        { id: 'certifications', label: 'Certifications', icon: '🎓' },
        { id: 'session-timeout', label: 'Session Timeout', icon: '⏱️' },
        { id: 'images', label: 'Image Management', icon: '🖼️' },
      ];

  const [activeTab, setActiveTab] = useState(() => {
    const requestedTab = searchParams.get('tab');
    const allowedIds = tabs.map(t => t.id);
    const defaultId = isHrAdminOnly ? 'users' : 'overview';
    return allowedIds.includes(requestedTab || '') ? requestedTab || defaultId : defaultId;
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  /*
   * Follow the ?tab= parameter when it CHANGES, not only on the first render.
   *
   * activeTab is seeded from the URL above, which is enough for somebody opening a bookmark.
   * It is not enough for a link followed from INSIDE the app — User Management's "Manage exit
   * formalities" navigates to ?tab=exit-formalities while this component is already mounted,
   * and without this the address bar changed and the pane did not.
   *
   * Deliberately keyed on the parameter alone. The sidebar buttons set activeTab without
   * touching the URL, so this cannot fire in response to them and cannot fight a click.
   */
  const requestedTab = searchParams.get('tab');
  useEffect(() => {
    if (requestedTab && tabs.some(t => t.id === requestedTab)) setActiveTab(requestedTab);
    // `tabs` is rebuilt every render; its identity is not the signal, the parameter is.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  if (!user?.role || !canManageUsers(user.role)) {
    return (
      <div className="settings-page">
        <div className="settings-page__denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className={`settings-page__sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <h3 className="settings-page__sidebar-title">{!sidebarCollapsed && 'Settings'}</h3>
        <nav className="settings-page__nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-page__nav-item ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              title={sidebarCollapsed ? tab.label : undefined}
            >
              <span className="settings-page__nav-icon">{tab.icon}</span>
              {!sidebarCollapsed && <span>{tab.label}</span>}
            </button>
          ))}
        </nav>
        <button
          className="settings-page__collapse-btn"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          aria-label={sidebarCollapsed ? 'Expand settings sidebar' : 'Collapse settings sidebar'}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points={sidebarCollapsed ? '9 18 15 12 9 6' : '15 18 9 12 15 6'} />
          </svg>
        </button>
      </div>
      <div className="settings-page__content">
        <React.Suspense fallback={<div className="settings-page__loading">Loading...</div>}>
          {activeTab === 'overview' && <SettingsOverview onNavigate={setActiveTab} />}
          {activeTab === 'users' && <UserManagement />}
          {activeTab === 'employee-info' && <EmployeeInformation />}
          {activeTab === 'exit-formalities' && <ExitFormalities />}
          {activeTab === 'org-lists' && <OrgListsSettings />}
          {/* Who may sign themselves in, and separately, the rules everybody is judged by. */}
          {activeTab === 'attendance' && <AttendanceSettings />}
          {activeTab === 'attendance-rules' && <AttendancePolicySettings />}
          {activeTab === 'email' && <EmailSettings />}
          {activeTab === 'nominations' && <NominationSettings />}
          {activeTab === 'certifications' && <CertificationSettings />}
          {activeTab === 'session-timeout' && <SessionTimeoutSettings />}
          {activeTab === 'images' && <ImageManagement />}
        </React.Suspense>
      </div>
    </div>
  );
};

/*
 * The Overview cards, one per destination tab.
 *
 * Derived from a list rather than hand-written markup because the two drifted: the cards
 * described three sections while the sidebar offered four, so Organisation Setup — the tab
 * that defines the Team and Employment Type values the User Management form offers — was
 * reachable only from the sidebar and invisible here.
 *
 * `id` is the tab id, so a card cannot point at a section that does not exist, and adding a
 * tab above is the only edit needed to have it appear here too.
 */
const OVERVIEW_CARDS: { id: string; icon: string; title: string; description: string }[] = [
  { id: 'users', icon: '👥', title: 'User Management', description: 'Add, edit, and manage user accounts, roles, and permissions.' },
  { id: 'org-lists', icon: '🏷️', title: 'Organisation Setup', description: 'Job titles, teams, employment types and the public holiday calendar.' },
  { id: 'exit-formalities', icon: '🚪', title: 'Exit Formalities', description: 'Resignation, notice, exit interview and the leaving checklist for everybody with an exit date.' },
  { id: 'attendance', icon: '🕒', title: 'Manual Attendance', description: 'Choose which employees may record their own attendance instead of it being read from the biometric device.' },
  { id: 'attendance-rules', icon: '📋', title: 'Attendance Rules', description: 'Required work hours and the regularization window, each with the date it takes effect.' },
  { id: 'email', icon: '📧', title: 'Email Settings', description: 'Configure SMTP settings for email notifications.' },
  { id: 'nominations', icon: '🏆', title: 'Nomination Settings', description: 'Select nomination receiver and CC recipients for new nomination notifications.' },
  { id: 'certifications', icon: '🎓', title: 'Certifications', description: 'Name one additional employee who may approve certification reimbursements, alongside the existing administrators.' },
  { id: 'session-timeout', icon: '⏱️', title: 'Session Timeout', description: 'Automatically sign out employees after a configurable period of inactivity, with a warning before logout.' },
];

const SettingsOverview: React.FC<{ onNavigate: (tabId: string) => void }> = ({ onNavigate }) => (
  <div className="settings-overview">
    <h2>Settings Overview</h2>
    <p>Manage your application settings from here. Select a card below, or use the sidebar.</p>
    <div className="settings-overview__cards">
      {OVERVIEW_CARDS.map(card => (
        /*
         * A real <button>, not a div with onClick. These were static divs that looked
         * interactive and did nothing when clicked. As buttons they are keyboard-reachable
         * and announced as actionable, which a clickable div is not.
         */
        <button
          key={card.id}
          type="button"
          className="settings-overview__card"
          onClick={() => onNavigate(card.id)}
        >
          <span className="settings-overview__card-icon">{card.icon}</span>
          <h3>{card.title}</h3>
          <p>{card.description}</p>
        </button>
      ))}
    </div>
  </div>
);

export default SettingsPage;
